// ── El índice de una página de documentación ────────────────────────────────
//
// Lo que en cualquier documentación que se lee de verdad va a la derecha: los
// apartados de ESTA página, para saltar a uno y para saber cuánto queda. Sin
// eso, una explicación de pantalla y media se lee con la rueda del ratón.
//
// Se saca de los encabezados ya dibujados y no del Markdown, y es a propósito:
// un tema puede ser un `.md` o un componente Vue —el parte por plantilla lo es—
// y así los dos tienen índice sin que ninguno tenga que declarar nada.
//
// Aquí sólo están las dos reglas que se pueden probar sin navegador. Quien mira
// el DOM es el armazón.

/**
 * El identificador de un apartado, para poder enlazarlo.
 *
 * Las tildes se van —`#producción` se copia y se pega mal por media internet— y
 * todo lo que no es letra ni número pasa a ser un guion.
 *
 * NO es el mismo cálculo que el del manual (`tools/manual/slug.js`, que
 * reimplementa el de VitePress paso por paso para que su menú lateral no lleve a
 * un sitio que no existe). Son dos documentos distintos y hoy ninguna dirección
 * salta de uno al otro; el día que el ERP enlace al manual —está previsto, en
 * `platform.help.manualUrl`— habrá que decidir si convergen, y será una decisión
 * con un test que lo sostenga, no un parecido de dos expresiones regulares.
 */
export function slugOf(text) {
  return String(text)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    // Un identificador que empieza por número no es un selector válido, y el
    // salto se hace con uno.
    .replace(/^(\d)/, '_$1')
}

/**
 * Los apartados de una página, con su identificador ya resuelto.
 *
 * Entra `[{ level, text }]` en el orden en que salen; sale lo mismo con `id` y
 * con el nivel normalizado a 1 o 2, que es lo único que un índice lateral puede
 * enseñar sin convertirse él mismo en un documento.
 *
 * Dos apartados con el mismo título son un caso real —«Si algo no cuadra» está
 * en todos los módulos, y dentro de una misma página se repite «Qué hace»— y sin
 * desempatar los dos enlaces llevarían al primero. El desempate va al final y
 * empieza por el 2, como hacen los demás.
 */
export function outlineOf(headings = []) {
  const usados = new Map()
  const niveles = [...new Set(headings.map(one => one.level))].sort((a, b) => a - b)

  return headings.map(({ level, text }) => {
    const base = slugOf(text)
    const visto = usados.get(base) ?? 0
    usados.set(base, visto + 1)

    return {
      // El nivel de la pantalla no es el del documento: lo que importa aquí es
      // si un apartado cuelga de otro, no si el renderizador lo dibujó como h4
      // o como h5.
      depth: Math.max(1, niveles.indexOf(level) + 1),
      text,
      id: visto ? `${base}-${visto + 1}` : base,
    }
  })
}
